import {createSlice} from '@reduxjs/toolkit';

const vehicleInfoSlice = createSlice({
  name: 'vehicleinfo',
  initialState: {
    loading: true,
    dataLoaded: false,
    sortMethod: 'vehiclename',
    vehicleData: [],
    displayedTable: 'attributes',
    onlyShowVehiclesWithoutManualEntry: false,
    showShopviewOnlyVehicle: false,
    showArchivedVehicles: false,
    deviceStatuses: {},
    groupModalOpened: false,
    newEquipModalOpen: false,
    newEquipModalRefreshTrigger: false,
    vehicleLastLocation: {},
    deviceHarnessTypes: null,
    devicePowerTypes: null,
    deviceIgnStates: null,
    deviceLastIgn: null,
    deviceCommStatuses: null,
    deviceGpsStatuses: null,
    setupStatusServiceTasks: null,
    setupStatusAssetLabels: null,
  },
  reducers: {
    updateLoading(state, action) {
      state.loading = action.payload;
    },
    updateSortMethod(state, action) {
      state.sortMethod = action.payload;
    },
    updateVehicleData(state, action) {
      state.vehicleData = action.payload;
    },
    updateDisplayedTable(state, action) {
      state.displayedTable = action.payload;
    },
    updateOnlyShowVehiclesWithoutManualEntry(state, action) {
      state.onlyShowVehiclesWithoutManualEntry = action.payload;
    },
    updateShowShopviewOnlyVehicle(state, action) {
      state.showShopviewOnlyVehicle = action.payload;
    },
    updateShowArchivedVehicles(state, action) {
      state.showArchivedVehicles = action.payload;
    },
    updateDeviceStatuses(state, action) {
      state.deviceStatuses = action.payload;
    },
    updateVehicleLastLocation(state, action) {
      state.vehicleLastLocation = action.payload;
    },
    setGroupModalOpened(state, action) {
      state.groupModalOpened = action.payload;
    },
    setNewEquipModalRefreshTrigger(state, action) {
      state.newEquipModalRefreshTrigger = action.payload;
    },
    setNewEquipModalOpen(state, action) {
      state.newEquipModalOpen = action.payload;
    },
    setDataLoaded(state, action) {
      state.dataLoaded = action.payload;
    },
    updateDeviceHarnessTypes(state, action) {
      state.deviceHarnessTypes = action.payload;
    },
    updateDevicePowerTypes(state, action) {
      state.devicePowerTypes = action.payload;
    },
    updateDeviceIgnStates(state, action) {
      state.deviceIgnStates = action.payload;
    },
    updateDeviceLastIgn(state, action) {
      state.deviceLastIgn = action.payload;
    },
    updateDeviceCommStatuses(state, action) {
      state.deviceCommStatuses = action.payload;
    },
    updateDeviceGpsStatuses(state, action) {
      state.deviceGpsStatuses = action.payload;
    },
    updateSetupStatusServiceTasks(state, action) {
      state.setupStatusServiceTasks = action.payload;
    },
    updateSetupStatusAssetLabels(state, action) {
      state.setupStatusAssetLabels = action.payload;
    },
  },
});

function getVehicleInfoData(options = {}) {
  const {forceRefresh = false, shouldGetDeviceData = true, refreshEngineHours = true, refreshOdometer = true} = options;

  return async (dispatch, getState) => {
    // Skip fetch if data already loaded and not forcing refresh
    const state = getState();
    if (state.vehicleinfo.dataLoaded && !forceRefresh) {
      dispatch(vehicleInfoSlice.actions.updateLoading(false));
      return;
    }

    const hasExistingData = state.vehicleinfo.vehicleData?.length > 0;
    if (!hasExistingData) {
      dispatch(vehicleInfoSlice.actions.updateLoading(true));
    }

    // Device status data - only fetch on initial load, not needed for vehicle updates
    if (shouldGetDeviceData) {
      fetch('/settings/getDeviceHarnessTypes', {cache: 'no-store'}).then(async (response) => {
        const harnessTypes = await response.json();
        dispatch(vehicleInfoSlice.actions.updateDeviceHarnessTypes(harnessTypes));
      });

      fetch('/settings/getDevicePowerTypes', {cache: 'no-store'}).then(async (response) => {
        const powerTypes = await response.json();
        dispatch(vehicleInfoSlice.actions.updateDevicePowerTypes(powerTypes.powerTypes));
        dispatch(vehicleInfoSlice.actions.updateDeviceIgnStates(powerTypes.ignStates));
        dispatch(vehicleInfoSlice.actions.updateDeviceLastIgn(powerTypes.ignData));
      });

      fetch('/settings/getVehiclesCommStatus', {cache: 'no-store'}).then(async (response) => {
        const commStatus = await response.json();
        dispatch(vehicleInfoSlice.actions.updateDeviceCommStatuses(commStatus));
      });

      fetch('/settings/getVehiclesGpsStatus', {cache: 'no-store'}).then(async (response) => {
        const gpsStatus = await response.json();
        dispatch(vehicleInfoSlice.actions.updateDeviceGpsStatuses(gpsStatus));
      });

      fetch('/settings/getVehiclesLastLocation', {cache: 'no-store'}).then(async (response) => {
        const lastLocation = await response.json();
        dispatch(vehicleInfoSlice.actions.updateVehicleLastLocation(lastLocation));
      });
    }

    const fetchPromises = [
      fetch('/settings/getAllVehicles', {cache: 'no-store'}),
      ...(refreshEngineHours ? [fetch('/shopview/getVehiclesEngineHours', {cache: 'no-store'})] : []),
      ...(refreshOdometer ? [fetch('/shopview/getVehiclesOdometer', {cache: 'no-store'})] : []),
    ];

    const engineHoursIndex = refreshEngineHours ? 1 : undefined;
    const odometerIndex = refreshOdometer ? (refreshEngineHours ? 2 : 1) : undefined;

    const responses = await Promise.all(fetchPromises);
    const vehicles = await responses[0].json();

    // Get current state for engine hours/odometer if not refreshing
    const currentVehicleData = state.vehicleinfo.vehicleData;
    const currentDataLookup = {};
    currentVehicleData.forEach((vehicle) => {
      currentDataLookup[vehicle.serialNumber] = {
        engineHoursValue: vehicle.engineHoursValue || 0,
        odometerValue: vehicle.odometerValue || 0,
      };
    });

    const engineHours = refreshEngineHours ? await responses[engineHoursIndex].json() : {};
    const odometer = refreshOdometer ? await responses[odometerIndex].json() : {};

    // Add engine hours and odometer, then sort
    vehicles.forEach((vehicle) => {
      const serialNumber = vehicle.serialNumber;
      const currentData = currentDataLookup[serialNumber] || {};

      vehicle.engineHoursValue = refreshEngineHours ? engineHours[serialNumber] || 0 : currentData.engineHoursValue;
      vehicle.odometerValue = refreshOdometer ? odometer[serialNumber] || 0 : currentData.odometerValue;
    });

    vehicles.sort((a, b) => {
      const aName = a.name?.toLowerCase() || '';
      const bName = b.name?.toLowerCase() || '';
      return aName.localeCompare(bName, undefined, {numeric: true, sensitivity: 'base'});
    });

    dispatch(vehicleInfoSlice.actions.updateVehicleData(vehicles));
    dispatch(vehicleInfoSlice.actions.setDataLoaded(true));
    dispatch(vehicleInfoSlice.actions.updateLoading(false));
  };
}

function getSetupStatusData() {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.vehicleinfo.setupStatusServiceTasks !== null && state.vehicleinfo.setupStatusAssetLabels !== null) {
      return;
    }
    const [serviceTasksRes, assetLabelsRes] = await Promise.all([
      fetch('/shopview/getServiceTasks', {cache: 'no-store'}),
      fetch('/getAssetLabels', {cache: 'no-store'}),
    ]);
    const serviceTasks = await serviceTasksRes.json();
    const assetLabels = await assetLabelsRes.json();
    dispatch(vehicleInfoSlice.actions.updateSetupStatusServiceTasks(serviceTasks));
    dispatch(vehicleInfoSlice.actions.updateSetupStatusAssetLabels(assetLabels));
  };
}

export {getVehicleInfoData, getSetupStatusData};
export const {
  updateLoading,
  updateSortMethod,
  updateVehicleData,
  updateDisplayedTable,
  updateOnlyShowVehiclesWithoutManualEntry,
  updateShowShopviewOnlyVehicle,
  updateShowArchivedVehicles,
  updateDeviceStatuses,
  updateVehicleLastLocation,
  setGroupModalOpened,
  setNewEquipModalRefreshTrigger,
  setNewEquipModalOpen,
  setDataLoaded,
  updateDeviceHarnessTypes,
  updateDevicePowerTypes,
  updateDeviceIgnStates,
  updateDeviceLastIgn,
  updateDeviceCommStatuses,
  updateDeviceGpsStatuses,
  updateSetupStatusServiceTasks,
  updateSetupStatusAssetLabels,
} = vehicleInfoSlice.actions;

export default vehicleInfoSlice.reducer;
